// Serenity Star API Client for DFS Collections Agent Portal
import { API_BASE_URL, API_KEY, DATASET_ID } from '../config/environment';

interface QueryResponse {
  columns: string[];
  rows: unknown[][];
  rowCount: number;
  executionTimeMs: number;
}

interface ChatResponse {
  message: string;
  sessionId?: string;
  metadata?: Record<string, unknown>;
}

interface AgentActivityResponse {
  response: string;
  success: boolean;
}

class SerenityClient {
  private baseUrl: string;
  private apiKey: string;
  private datasetId: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
    this.apiKey = API_KEY;
    this.datasetId = DATASET_ID;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      'X-API-KEY': this.apiKey,
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }

    return response.json();
  }

  // Execute SQL query on dataset
  async query(sql: string): Promise<QueryResponse> {
    return this.request<QueryResponse>(
      `/api/v2/Dataset/${this.datasetId}/query`,
      {
        method: 'POST',
        body: JSON.stringify({ sql, format: 'application/json' }),
      }
    );
  }

  // Query data from dataset tables and return as objects
  async queryData<T>(sql: string): Promise<T[]> {
    const result = await this.query(sql);
    if (!result.rows || result.rows.length === 0) return [];
    
    return result.rows.map(row => {
      const obj: Record<string, unknown> = {};
      result.columns.forEach((col, idx) => {
        obj[col] = row[idx];
      });
      return obj as T;
    });
  }

  // Insert data into a table using SQL
  async insertData<T>(tableName: string, data: Partial<T>): Promise<void> {
    const columns = Object.keys(data);
    const values = columns.map(col => {
      const val = (data as Record<string, unknown>)[col];
      if (val === null || val === undefined) return 'NULL';
      if (typeof val === 'string') return `'${val.replace(/'/g, "''")}'`;
      return String(val);
    });
    
    const sql = `INSERT INTO ${tableName} (${columns.join(', ')}) VALUES (${values.join(', ')})`;
    await this.query(sql);
  }

  // Update data in a table using SQL
  async updateData<T>(
    tableName: string,
    idColumn: string,
    rowId: string,
    data: Partial<T>
  ): Promise<void> {
    const updates = Object.entries(data)
      .filter(([, val]) => val !== undefined)
      .map(([col, val]) => {
        if (val === null) return `${col} = NULL`;
        if (typeof val === 'string') return `${col} = '${val.replace(/'/g, "''")}'`;
        return `${col} = ${val}`;
      })
      .join(', ');
    
    const sql = `UPDATE ${tableName} SET ${updates} WHERE ${idColumn} = '${rowId}'`;
    await this.query(sql);
  }

  // Delete data from a table using SQL
  async deleteData(tableName: string, idColumn: string, rowId: string): Promise<void> {
    const sql = `DELETE FROM ${tableName} WHERE ${idColumn} = '${rowId}'`;
    await this.query(sql);
  }

  // Chat with an AI agent (assistant type)
  async chat(
    agentCode: string,
    message: string,
    sessionId?: string
  ): Promise<ChatResponse> {
    const body: Record<string, unknown> = {
      message,
    };
    if (sessionId) {
      body.sessionId = sessionId;
    }

    return this.request<ChatResponse>(`/api/v2/agent/${agentCode}/chat`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Execute an activity agent
  async executeAgent(
    agentCode: string,
    parameters: Array<{ Key: string; Value: string }>
  ): Promise<AgentActivityResponse> {
    return this.request<AgentActivityResponse>(`/api/v2/agent/${agentCode}/execute`, {
      method: 'POST',
      body: JSON.stringify(parameters),
    });
  }

  // Execute agent with context (helper)
  async executeAgentWithContext(
    agentCode: string,
    context: {
      caseId?: string;
      debtorId?: string;
      documentId?: string;
      additionalData?: Record<string, unknown>;
    }
  ): Promise<AgentActivityResponse> {
    const params = Object.entries(context)
      .filter(([, val]) => val !== undefined)
      .map(([key, val]) => ({
        Key: key,
        Value: typeof val === 'string' ? val : JSON.stringify(val),
      }));
    return this.executeAgent(agentCode, params);
  }

  // Get dataset info
  async getDatasetInfo(): Promise<{ id: string; identifier: string; tables: unknown[] }> {
    return this.request(`/api/v2/Dataset/${this.datasetId}`);
  }
}

export const serenityClient = new SerenityClient();
